import { useEffect, useRef, useState } from "react";
import { fetchWidgetPreview, WidgetPreviewResponse } from "@/lib/widget-api";
import { executeToolApi } from "@/lib/mcp-tools-api";
import { readResource } from "@/lib/mcp-resources-api";

export interface WidgetPreviewProps {
  canonicalWidgetId: string;
}

interface JsonRpcMessage {
  jsonrpc: "2.0";
  method: string;
  params?: unknown;
}

export function WidgetPreview({ canonicalWidgetId }: WidgetPreviewProps) {
  const [preview, setPreview] = useState<WidgetPreviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sandboxReady, setSandboxReady] = useState(false);

  const sandboxRef = useRef<HTMLIFrameElement | null>(null);

  // Fetch preview data when canonicalWidgetId changes
  useEffect(() => {
    let cancelled = false;

    async function loadPreview() {
      setLoading(true);
      setError(null);
      setPreview(null);
      setSandboxReady(false);

      try {
        const data = await fetchWidgetPreview(canonicalWidgetId);
        if (!cancelled) {
          setPreview(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Unknown error");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadPreview();

    return () => {
      cancelled = true;
    };
  }, [canonicalWidgetId]);

  // Send HTML to sandbox when both conditions are met:
  // 1. Sandbox is ready
  // 2. Preview data is available
  useEffect(() => {
    if (sandboxReady && preview && sandboxRef.current?.contentWindow) {
      const message: JsonRpcMessage = {
        jsonrpc: "2.0",
        method: "ui/sandbox-resource-ready",
        params: { html: preview.html },
      };

      sandboxRef.current.contentWindow.postMessage(message, "*");
    }
  }, [sandboxReady, preview]);

  // Set up postMessage listener
  useEffect(() => {
    async function handleCallServerTool(
      id: string | number | undefined,
      params: any,
    ) {
      if (!preview) return;

      try {
        const toolName = params?.name;
        const toolArgs = params?.arguments ?? {};

        if (!toolName) {
          if (id != null) {
            window.postMessage(
              {
                jsonrpc: "2.0",
                id,
                error: { message: "Missing tool name" },
              },
              "*",
            );
          }
          return;
        }

        // Call the MCP tool via our existing API
        const result = await executeToolApi(
          preview.serverId,
          toolName,
          toolArgs,
        );

        // Send JSON-RPC response to resolve the callServerTool() Promise
        if (id != null) {
          window.postMessage(
            {
              jsonrpc: "2.0",
              id,
              result,
            },
            "*",
          );
        }

        // Also send a notification so app.ontoolresult() is called
        const notifyMsg = {
          jsonrpc: "2.0",
          method: "mcp.ext-apps/notifyToolResult",
          params: result,
        };
        console.log("[WidgetPreview] Sending notifyToolResult:", notifyMsg);
        window.postMessage(notifyMsg, "*");
      } catch (error: any) {
        console.error("[WidgetPreview] handleCallServerTool error", error);
        if (id != null) {
          window.postMessage(
            {
              jsonrpc: "2.0",
              id,
              error: { message: error?.message || "Failed to call tool" },
            },
            "*",
          );
        }
      }
    }

    async function handleReadServerResource(
      id: string | number | undefined,
      params: any,
    ) {
      if (!preview) return;

      try {
        const uri = params?.uri;

        if (!uri) {
          if (id != null) {
            window.postMessage(
              {
                jsonrpc: "2.0",
                id,
                error: { message: "Missing resource uri" },
              },
              "*",
            );
          }
          return;
        }

        // Read the MCP resource via our existing API
        const result = await readResource(preview.serverId, uri);

        if (id != null) {
          window.postMessage(
            {
              jsonrpc: "2.0",
              id,
              result,
            },
            "*",
          );
        }
      } catch (error: any) {
        console.error("[WidgetPreview] handleReadServerResource error", error);
        if (id != null) {
          window.postMessage(
            {
              jsonrpc: "2.0",
              id,
              error: { message: error?.message || "Failed to read resource" },
            },
            "*",
          );
        }
      }
    }

    function handleMessage(event: MessageEvent) {
      const data = event.data;

      // Ignore invalid messages
      if (!data || typeof data !== "object") {
        return;
      }

      // Handle sandbox-ready message
      if (data.method === "ui/sandbox-ready") {
        setSandboxReady(true);
        return;
      }

      // Handle MCP Apps JSON-RPC messages
      if (data.jsonrpc === "2.0" && typeof data.method === "string") {
        const { method, id, params } = data;

        switch (method) {
          case "mcp.ext-apps/callServerTool":
            void handleCallServerTool(id, params);
            return;

          case "mcp.ext-apps/readServerResource":
            void handleReadServerResource(id, params);
            return;

          case "mcp.ext-apps/sendMessage":
            console.debug("[MCP Apps] sendMessage", params);
            return;

          case "mcp.ext-apps/openLink":
            {
              const url = (params && params.url) || params;
              if (typeof url === "string") {
                window.open(url, "_blank", "noopener,noreferrer");
              }
            }
            return;

          default:
            // Log other MCP Apps message types for debugging
            if (method.startsWith("mcp.ext-apps/")) {
              console.log("[WidgetPreview] Unhandled MCP Apps message:", data);
            }
            break;
        }
      }

      // Log other message types for debugging
      if (data.method) {
        console.log("[WidgetPreview] Received message:", data);
      }
    }

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [preview]);

  if (loading) {
    return (
      <div className="p-4">
        <p className="text-sm text-muted-foreground">
          Loading widget preview...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <p className="text-sm text-destructive">Error: {error}</p>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="p-4">
        <p className="text-sm text-muted-foreground">No preview available</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 h-full">
      {/* Widget metadata section */}
      <div className="p-4 border rounded-lg bg-card">
        <h3 className="font-semibold text-lg mb-2">
          {preview.widget.displayName}
        </h3>
        <div className="space-y-1 text-sm text-muted-foreground">
          <div>
            <span className="font-medium">URI:</span> {preview.resource.uri}
          </div>
          {preview.resource.mimeType && (
            <div>
              <span className="font-medium">MIME Type:</span>{" "}
              {preview.resource.mimeType}
            </div>
          )}
          {preview.widget.description && (
            <div>
              <span className="font-medium">Description:</span>{" "}
              {preview.widget.description}
            </div>
          )}
          {preview.widget.widgetKind && (
            <div>
              <span className="font-medium">Kind:</span>{" "}
              {preview.widget.widgetKind}
            </div>
          )}
          {preview.ui && (
            <div>
              <span className="font-medium">UI Metadata:</span>{" "}
              {JSON.stringify(preview.ui)}
            </div>
          )}
        </div>
      </div>

      {/* Sandbox iframe */}
      <div className="flex-1 border rounded-lg overflow-hidden">
        <iframe
          ref={sandboxRef}
          src="/mcp-ui-sandbox.html"
          sandbox="allow-scripts allow-same-origin allow-popups"
          style={{
            width: "100%",
            height: "100%",
            border: "none",
          }}
          title="Widget Preview Sandbox"
        />
      </div>
    </div>
  );
}
